import { useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { DataContext } from "@/models/DataProvider";
import { ipcRendererCallback, isElectron, removeIpcListener } from "@/utils";

/**
 * Applies navigation on the next turn so LiveQueries and other DataProvider
 * descendants have run their effects after `bootstrapComplete` flips true.
 */
const scheduleNavigate = (navigate: (path: string) => void, path: string) => {
  window.setTimeout(() => navigate(path), 0);
};

export default function ElectronDeepLinkHandler() {
  const navigate = useNavigate();
  const { bootstrapComplete } = useContext(DataContext);
  const pendingPathRef = useRef<string | null>(null);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!isElectron) return;

    const handler = (_: unknown, url: string) => {
      if (!url) return;
      if (!bootstrapComplete) {
        pendingPathRef.current = url;
        return;
      }
      scheduleNavigate((path) => navigateRef.current(path), url);
    };

    ipcRendererCallback("deep-link-detected", handler);
    return () => {
      removeIpcListener("deep-link-detected", handler);
    };
  }, [bootstrapComplete]);

  useEffect(() => {
    if (!isElectron || !bootstrapComplete) return;
    const pending = pendingPathRef.current;
    if (!pending) return;
    pendingPathRef.current = null;
    scheduleNavigate((path) => navigateRef.current(path), pending);
  }, [bootstrapComplete]);

  return null;
}
